import type { JSX } from 'react'
import type { MatchStatus as MatchStatusType } from '@/lib/live/state'

interface Props {
  status: MatchStatusType
  isTiebreak?: boolean
  currentSet?: number
  className?: string
  /**
   * Corpo delle etichette. Il tabellone «card» la passa scalata col contenitore
   * (`cqi`), così su un tabellone largo le scritte non restano piccole; senza
   * vale la misura fissa del componente (11px), che è quella della regia.
   */
  fontSize?: string
  /**
   * Testo che prende il posto dello stato della gara (es. «Sostituzione»):
   * durante un evento in corso la riga di stato dice l'evento, non «In corso».
   * Con l'override sparisce anche «· Set N».
   */
  overrideLabel?: string | null
  /**
   * Fondo su cui è disegnata l'etichetta. Il tabellone «card» lo passa già
   * (`ScorePanel`: `tone`), così i colori di stato possono avere la coppia
   * giusta: su fondo scuro servono tinte chiare, su fondo chiaro tinte scure.
   * Un colore solo non può passare il contrasto su entrambi (misurato: il rosso
   * `#ef4444` stava a 4,42:1 sul board scuro e 3,38:1 su quello chiaro).
   */
  tone?: 'dark' | 'light'
}

function IconScheduled() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  )
}
function IconWarmup() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
    </svg>
  )
}
function IconLive() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5,3 19,12 5,21"/>
    </svg>
  )
}
function IconPause() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>
    </svg>
  )
}
function IconTimeout() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/><polyline points="12,6 12,12 16,14"/>
    </svg>
  )
}
function IconFinished() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12"/>
    </svg>
  )
}
function IconPostponed() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  )
}
function IconCancelled() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/>
    </svg>
  )
}

/**
 * Colori per stato e per fondo, con il contrasto **misurato** sui due sfondi
 * reali del tabellone (`--sb-main-bg`: `#171e2e` scuro, `#eef3fa` il punto più
 * chiaro della scala chiara). Soglia AA per testo normale, che è il caso: la
 * label è 11–20 px in maiuscoletto, non un «large text» da 18,66 px bold.
 *
 *   dark  → live #f87171 6,02:1 · arancio #FE7200 6,04:1 · ardesia #8b88b8 5,00:1
 *   light → live #b91c1c 5,80:1 · arancio #a8480a 5,24:1 · ardesia #676593 4,88:1
 *
 * Prima c'era un colore solo: il rosso `#ef4444` stava a 4,42:1 sul board scuro
 * e a 3,38:1 su quello chiaro (sotto AA in tutti e due), l'ardesia `#676593` a
 * 3,06:1 sul board scuro.
 */
const STATUS_COLORS: Record<'dark' | 'light', Record<MatchStatusType, string>> = {
  dark: {
    scheduled: '#8b88b8', warmup: '#FE7200', live: '#f87171', pause: '#FE7200',
    timeout_home: '#FE7200', timeout_away: '#FE7200',
    finished: '#8b88b8', postponed: '#8b88b8', cancelled: '#8b88b8',
  },
  light: {
    scheduled: '#676593', warmup: '#a8480a', live: '#b91c1c', pause: '#a8480a',
    timeout_home: '#a8480a', timeout_away: '#a8480a',
    finished: '#676593', postponed: '#676593', cancelled: '#676593',
  },
}

const STATUS_CONFIG: Record<MatchStatusType, {
  label: string
  pulse?: boolean
  Icon: () => JSX.Element
}> = {
  scheduled:     { label: 'Programmata',    Icon: IconScheduled },
  warmup:        { label: 'Riscaldamento',  pulse: true,  Icon: IconWarmup },
  live:          { label: 'In corso',       pulse: true,  Icon: IconLive },
  pause:         { label: 'Pausa',          Icon: IconPause },
  timeout_home:  { label: 'Timeout',        pulse: true,  Icon: IconTimeout },
  timeout_away:  { label: 'Timeout',        pulse: true,  Icon: IconTimeout },
  finished:      { label: 'Conclusa',       Icon: IconFinished },
  postponed:     { label: 'Rinviata',       Icon: IconPostponed },
  cancelled:     { label: 'Annullata',      Icon: IconCancelled },
}

/**
 * Stati in cui il set corrente è un'informazione vera. In pausa e in timeout il
 * set in corso **esiste** (la palla è ferma, non il set): prima «Set 3» spariva
 * proprio nei due momenti in cui lo spettatore lo cerca. Fuori restano
 * programmata/riscaldamento (il set non è cominciato) e gli esiti.
 */
const SET_VISIBLE: ReadonlySet<MatchStatusType> = new Set<MatchStatusType>([
  'live', 'pause', 'timeout_home', 'timeout_away',
])

export default function MatchStatus({ status, isTiebreak, currentSet, className = '', fontSize, tone = 'dark', overrideLabel = null }: Props) {
  const cfg = STATUS_CONFIG[status]
  const { Icon } = cfg
  // Un evento in corso (la sostituzione) prende il posto della stato della gara:
  // «In corso» resta vero, ma in quel momento la cosa da leggere è un'altra.
  const label = overrideLabel ?? cfg.label
  const color = STATUS_COLORS[tone][status]
  const pulse = cfg.pulse || overrideLabel != null

  const setLabel = overrideLabel != null
    ? null
    : currentSet
      ? isTiebreak ? 'Tiebreak' : `Set ${currentSet}`
      : null

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {pulse ? (
        <span className="relative flex h-2 w-2 flex-shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: color }} />
          <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: color }} />
        </span>
      ) : (
        <span style={{ color }} className="flex-shrink-0">
          <Icon />
        </span>
      )}
      <span
        className="font-display font-bold uppercase tracking-widest text-[11px]"
        style={{ color, fontSize }}
      >
        {label}
      </span>
      {setLabel && SET_VISIBLE.has(status) && (
        <>
          {/* Separatore e «Set N» seguono il tema del tabellone: erano bianchi
              fissi, invisibili sul tabellone chiaro. */}
          <span className="text-[11px]" style={{ fontSize, color: 'var(--sb-text-muted, rgba(255,255,255,0.3))' }}>·</span>
          <span
            className="font-display font-bold uppercase tracking-widest text-[11px]"
            style={{ fontSize, color: 'var(--sb-text-soft, rgba(255,255,255,0.6))' }}
          >
            {setLabel}
          </span>
        </>
      )}
    </div>
  )
}
